import catalogJson from './portal-ui-catalog.json';
import vocabularyJson from './vocabulary.json';

/**
 * Typed access to what the in-house agent may know and touch in the portal.
 *
 * Both JSON files are generated — do not edit them here:
 * - `portal-ui-catalog.json` ← shared/agent/portal-ui-catalog.json
 *   (scripts/sync_portal_ui_catalog.py): pages, anchors, forms, dialogs, tours
 * - `vocabulary.json` ← the routes' @capability specs
 *   (scripts/build_capability_manifest.py): entity types, refresh resources,
 *   risk classes, and the UI part of every capability
 *
 * Everything the agent sends to the browser is checked against these lists:
 * an id that is not in them does nothing. See
 * features/docs/technical/portal-agent-native.md.
 */

export interface AgentCatalogPage {
  route: string;
  area: string;
  purpose: string;
  /** route parameter → entity type, e.g. `{ tutorId: 'bot' }` */
  params?: Record<string, string>;
  /** route parameter that holds the active tab or section */
  tab_param?: string;
  tabs?: string[];
  tasks?: string[];
  actions?: string[];
  help?: string;
}

export interface AgentCatalogAnchor {
  description: string;
  /** structural anchor: resolved by this selector (written by us, never by the agent) */
  selector?: string;
  /** page anchor: rendered as `data-al-anchor` on that page */
  page?: string;
}

export interface AgentAnchorFamily {
  pattern: string;
  description: string;
  selector?: string;
}

export interface AgentCatalog {
  version: number;
  pages: Record<string, AgentCatalogPage>;
  anchors: Record<string, AgentCatalogAnchor>;
  anchor_families: Record<string, AgentAnchorFamily>;
  forms: Record<string, unknown>;
  dialogs: Record<string, unknown>;
  tours: Record<string, unknown>;
}

export interface AgentVocabularyAction {
  area: string;
  risk: string;
  entity_types: string[];
  ui_target: string | null;
  refresh: string[];
}

export interface AgentVocabulary {
  risks: string[];
  areas: string[];
  entity_types: string[];
  refresh_resources: string[];
  ui_elements: string[];
  actions: Record<string, AgentVocabularyAction>;
}

export const AGENT_CATALOG = catalogJson as unknown as AgentCatalog;
export const AGENT_VOCABULARY = vocabularyJson as unknown as AgentVocabulary;

/** An object the user is looking at or has selected, e.g. `{ type: 'bot', id: '…' }`. */
export interface AgentEntityRef {
  type: string;
  id: string;
}

const ENTITY_TYPES = new Set(AGENT_VOCABULARY.entity_types);
const UI_ELEMENTS = new Set(AGENT_VOCABULARY.ui_elements);
const REFRESH_RESOURCES = new Set(AGENT_VOCABULARY.refresh_resources);

/** Ids of objects: UUIDs, numeric ids and the odd slug (`new`). */
export const AGENT_OBJECT_ID = /^[A-Za-z0-9_-]{1,64}$/;
/** The value part of a family anchor: a setting's variable_name, or a page id for `nav.` (page ids have dots). */
const FAMILY_VALUE = /^[A-Za-z0-9_.-]{1,80}$/;

export function isEntityType(type: string): boolean {
  return ENTITY_TYPES.has(type);
}

export function isUiElement(element: string): boolean {
  return UI_ELEMENTS.has(element);
}

export function isRefreshResource(resource: string): boolean {
  return REFRESH_RESOURCES.has(resource);
}

export function entityRef(type: string, id: unknown): AgentEntityRef | null {
  const value = id === null || id === undefined ? '' : String(id);
  return isEntityType(type) && AGENT_OBJECT_ID.test(value) ? { type, id: value } : null;
}

/** Refs for the ids that are valid — for a page's selection (`entityRefs('bucket', [this.selected?.id])`). */
export function entityRefs(type: string, ids: unknown[]): AgentEntityRef[] {
  return ids.map((id) => entityRef(type, id)).filter((ref): ref is AgentEntityRef => ref !== null);
}

/** `bot:<id>:card` — the value of `data-agent-target`, or null if any part is unknown. */
export function agentTargetValue(type: string, id: unknown, element: string): string | null {
  const ref = entityRef(type, id);
  return ref && isUiElement(element) ? `${ref.type}:${ref.id}:${element}` : null;
}

/** The family of an anchor id (`setting.default_language` → `setting`), if it is one. */
export function anchorFamilyOf(anchorId: string): { family: string; value: string } | null {
  const dot = anchorId.indexOf('.');
  if (dot <= 0) return null;
  const family = anchorId.slice(0, dot);
  const value = anchorId.slice(dot + 1);
  return AGENT_CATALOG.anchor_families[family] && FAMILY_VALUE.test(value) ? { family, value } : null;
}

export function isKnownAnchor(anchorId: string): boolean {
  return !!AGENT_CATALOG.anchors[anchorId] || anchorFamilyOf(anchorId) !== null;
}
